import '../Main.scss';

import * as React from 'react';
import { connect } from 'react-redux';
import {
    Col,
    Container,
    Row
} from 'reactstrap';

import { AccountingPackage } from '../../Models/Api/strongbox.financialportal';
import { PortalTextImportModals } from '../../Models/Api/strongbox.financialportal';
import { ApplicationState } from '../../Store';
import { GetFilteredAccountingPackages } from '../../Store/AppSettings';
import { GetPortalText } from '../../Store/PortalAssets';
import { GetBasicSettings, KnownTenants } from '../../Store/Tenant';
import { UniversalCollectorAccountingPackageNameId } from '../../Models/Api/AccountingPackage';
import { AccountingPkgPresentation } from './AccountingPkgPresentation';
import { AccountingSystemGroup } from './AccountingSystemGroup';

type InjectedReduxState = {
    tenantSelectPkgPrompt: string;
    accountingPackages: AccountingPackage[];
}

type InjectedActionCreators = {
}

type SelectAccountingSystemProps = {
    buttonsDisabled: boolean;
    onLink: (accountingPkg: AccountingPkgPresentation) => void;
    rowClass?: string;
    tabIndexBase: number;
    immediateConnectPackage?: string;
};

type Props = SelectAccountingSystemProps & InjectedActionCreators & InjectedReduxState;

export const SelectAccountingSystemComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        accountingPackages,
        buttonsDisabled,
        onLink,
        tenantSelectPkgPrompt,
        tabIndexBase,
        immediateConnectPackage,
    } = props;

    const immediateAccountingPackage: AccountingPkgPresentation | undefined = React.useMemo(() => {
        if (!immediateConnectPackage) {
            return undefined;
        }

        return {
            nameId: immediateConnectPackage,
            featureName: immediateConnectPackage,
            descriptor: immediateConnectPackage,
            onPkgSelected: onLink,
        }

        // immediateConectPackage is all that's necessary here.
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [immediateConnectPackage])

    const activeAccountingPackages: AccountingPkgPresentation[] = React.useMemo(() => {
        // We treat the universal collector accounting package specially.
        const excludingUniversalCollector = accountingPackages
            .filter(availablePackage => availablePackage.nameId !== UniversalCollectorAccountingPackageNameId)
            .sort((p1, p2) => p1.marketingSortOrder - p2.marketingSortOrder);
        return excludingUniversalCollector.map((x): AccountingPkgPresentation => {
            return {
                ...x,
                featureName: x.nameId ?? '',
                descriptor: x.friendlyName ?? '',
                onPkgSelected: onLink
            }
        })
    }, [accountingPackages, onLink])

    return (
        <Container
            fluid
            className={`accounting-button-container centered-col control-region`}
        >
            <Row>
                <Col xs={12} md={8} className={'accounting-button-group-column'}>
                    <AccountingSystemGroup
                        accountingSystems={activeAccountingPackages}
                        id={'accountingPackages'}
                        name={tenantSelectPkgPrompt}
                        disabled={buttonsDisabled}
                        tabIndexBase={tabIndexBase}
                        immediateConnectPkg={immediateAccountingPackage}
                    />
                </Col>
            </Row>
        </Container>
    );
}

export const SelectAccountingSystem = connect<InjectedReduxState, InjectedActionCreators, SelectAccountingSystemProps, ApplicationState>(
    (appState: ApplicationState) => {
        const basicSettings = GetBasicSettings(appState);
        const importText = GetPortalText<PortalTextImportModals>(appState, 'commonImport');

        let tenantSelectPkgPrompt = importText?.selectAccountingSystemAccountant || '';
        if (!!basicSettings && basicSettings.defaultResource === KnownTenants.defaultLender) {
            tenantSelectPkgPrompt = importText?.selectAccountingSystemLender || '';
        }

        const result = {
            tenantSelectPkgPrompt,
            accountingPackages: GetFilteredAccountingPackages(appState)
        };

        return result;
    }
)(SelectAccountingSystemComponent);


